/**
 * A person's message, as the composer writes it and their bubble draws it:
 * lines of text with `inline code` in them, fenced code blocks, and lists.
 * Nothing else of Markdown is read, so a person's `*` or `#` stays as they
 * typed it.
 *
 * The fences follow `markdownParse.ts`: ``` or ~~~, three or more, closed
 * by a run of the same character at least as long. One left open runs to the
 * end. An inline span is closed by a run of backticks exactly as long as the
 * one that opened it, on the same line; an unmatched run stays as typed.
 */

export interface Span {
  code: boolean;
  text: string;
}

/** A list: its items' lines, and the lists nested under each. */
export interface List {
  ordered: boolean;
  /** The first item's number; 1 for a bulleted list. */
  start: number;
  items: Array<{ spans: Span[]; lists: List[] }>;
}

export type Piece = { t: 'line'; spans: Span[] } | { t: 'fence'; lang: string; text: string } | { t: 'list'; list: List };

const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})[ \t]*([^\s`]*)[^`]*$/;
const FENCE_CLOSE = /^ {0,3}(`{3,}|~{3,})[ \t]*$/;
/** `- item`, `* item` or `3. item`: the indent, the marker and the item. */
const LIST_ITEM = /^( *)([-*]|\d{1,9}\.) (.*)$/;

/** A line's spans: its code, and the text between. */
export function parseSpans(line: string): Span[] {
  const out: Span[] = [];
  let text = '';
  let i = 0;
  while (i < line.length) {
    if (line[i] !== '`') {
      text += line[i];
      i++;
      continue;
    }
    let n = 0;
    while (line[i + n] === '`') n++;
    // The closing run: exactly n backticks, not part of a longer run.
    let close = -1;
    for (let j = i + n; j < line.length; ) {
      if (line[j] !== '`') {
        j++;
        continue;
      }
      let m = 0;
      while (line[j + m] === '`') m++;
      if (m === n) {
        close = j;
        break;
      }
      j += m;
    }
    if (close < 0) {
      text += line.slice(i, i + n);
      i += n;
      continue;
    }
    let code = line.slice(i + n, close);
    // One space either side is padding, when both are there and it is not
    // all spaces: how a span that starts or ends with a backtick is written.
    if (code.startsWith(' ') && code.endsWith(' ') && code.trim()) code = code.slice(1, -1);
    if (text) out.push({ code: false, text });
    text = '';
    out.push({ code: true, text: code });
    i = close + n;
  }
  if (text) out.push({ code: false, text });
  return out;
}

/**
 * The list lines from `i` on, as lists, and the index of the first line that
 * is not one of them. An item nests under the one before when it is indented
 * to that item's text; a blank line, or any other line, ends the list. A
 * change between bullets and numbers starts a new list.
 */
function parseLists(lines: string[], i: number): { lists: List[]; next: number } {
  const lists: List[] = [];
  // The lists open at each depth: where their markers sit and their text starts.
  const open: Array<{ list: List; indent: number; content: number }> = [];
  for (; i < lines.length; i++) {
    const m = LIST_ITEM.exec(lines[i]!);
    if (!m || (!open.length && m[1]!.length > 3)) break;
    const indent = m[1]!.length;
    const ordered = m[2] !== '-' && m[2] !== '*';
    const item = { spans: parseSpans(m[3]!), lists: [] as List[] };
    const content = indent + m[2]!.length + 1;
    while (open.length && indent < open[open.length - 1]!.indent) open.pop();
    const top = open[open.length - 1];
    if (top && indent < top.content) {
      if (top.list.ordered === ordered) {
        top.list.items.push(item);
        top.content = content;
        continue;
      }
      open.pop();
    }
    const list: List = { ordered, start: ordered ? parseInt(m[2]!, 10) : 1, items: [item] };
    const parent = open[open.length - 1];
    if (parent) parent.list.items[parent.list.items.length - 1]!.lists.push(list);
    else lists.push(list);
    open.push({ list, indent, content });
  }
  return { lists, next: i };
}

export function parseCodeText(text: string): Piece[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const out: Piece[] = [];
  for (let i = 0; i < lines.length; i++) {
    const open = FENCE_OPEN.exec(lines[i]!);
    if (!open) {
      const { lists, next } = parseLists(lines, i);
      if (lists.length) {
        for (const list of lists) out.push({ t: 'list', list });
        i = next - 1;
      } else out.push({ t: 'line', spans: parseSpans(lines[i]!) });
      continue;
    }
    const fence = open[1]!;
    const body: string[] = [];
    for (i++; i < lines.length; i++) {
      const close = FENCE_CLOSE.exec(lines[i]!);
      if (close && close[1]![0] === fence[0] && close[1]!.length >= fence.length) break;
      body.push(lines[i]!);
    }
    out.push({ t: 'fence', lang: open[2] ?? '', text: body.join('\n') });
  }
  return out;
}

const longestRun = (text: string, ch: string): number => {
  let longest = 0;
  let run = 0;
  for (const c of text) {
    run = c === ch ? run + 1 : 0;
    if (run > longest) longest = run;
  }
  return longest;
};

/** Code as an inline span: fenced by more backticks than it holds, and
 *  padded where it starts or ends with one. Empty code is nothing. */
export function inlineCode(code: string): string {
  if (!code) return '';
  const fence = '`'.repeat(longestRun(code, '`') + 1);
  const pad = code.startsWith('`') || code.endsWith('`') || (code.startsWith(' ') && code.endsWith(' ') && code.trim() !== '');
  return pad ? `${fence} ${code} ${fence}` : `${fence}${code}${fence}`;
}

/** Code as a fenced block, fenced by more backticks than it holds. */
export function fenced(lang: string, code: string): string {
  const fence = '`'.repeat(Math.max(3, longestRun(code, '`') + 1));
  return `${fence}${lang}\n${code ? `${code}\n` : ''}${fence}`;
}
